import { cn } from '@/lib/utils'
import { IconCheck, IconLock, IconWhatsApp } from '@/components/Icons'

export function LiveDot({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn('relative flex size-2.5', className)}>
      <span className="live-ring absolute inline-flex size-full rounded-full bg-red-400" />
      <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
    </span>
  )
}

export function OnlineDot({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn('relative flex size-2.5', className)}>
      <span className="live-ring absolute inline-flex size-full rounded-full bg-green-400" />
      <span className="relative inline-flex size-2.5 rounded-full bg-green-400" />
    </span>
  )
}

export function AgentChat({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'glass w-[272px] rounded-2xl p-3.5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]',
        className,
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-white/[0.06] pb-3">
        <span className="flex size-8 items-center justify-center rounded-lg bg-[#25D366]/15 text-[#25D366]">
          <IconWhatsApp size={16} />
        </span>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[var(--text-strong)]">WhatsApp AI agent</p>
          <p className="font-code text-[10px] text-[var(--surface-muted)]">
            what you&apos;ll build · n8n + OpenAI
          </p>
        </div>
        <OnlineDot className="ml-auto" />
      </div>
      <div className="space-y-2 pt-3 text-[12px] leading-snug">
        <p className="w-fit max-w-[200px] rounded-xl rounded-tl-sm bg-white/[0.07] px-3 py-2 text-[var(--surface-fg)]">
          Do you deliver to Zindabazar today?
        </p>
        <p className="ml-auto w-fit max-w-[210px] rounded-xl rounded-tr-sm bg-[linear-gradient(135deg,rgba(0,229,255,0.24),rgba(0,229,255,0.1))] px-3 py-2 text-[#d8fbff]">
          Yes! Orders before 6 PM arrive today. Want me to book one?
        </p>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-3 py-2">
          <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
          <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
          <span className="typing-dot size-1.5 rounded-full bg-[var(--surface-muted)]" />
        </span>
      </div>
    </div>
  )
}

export function ClassCallMock({ className }: { className?: string }) {
  const tiles = ['RA', 'SN', 'TK', 'MH', 'FA', 'JI']
  return (
    <div
      aria-hidden="true"
      className={cn('rounded-2xl border border-white/[0.07] bg-black/40 p-2.5', className)}
    >
      <div className="relative flex h-24 items-end justify-between rounded-xl bg-[radial-gradient(circle_at_50%_30%,rgba(0,229,255,0.25),transparent_60%),linear-gradient(180deg,#14181d,#0c0e11)] p-2">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-black/50 px-2 py-1 font-code text-[9px] font-semibold uppercase tracking-wider text-red-300">
          <LiveDot className="size-2" /> Live
        </span>
        <span className="rounded-md bg-black/50 px-2 py-1 font-code text-[9px] text-[var(--surface-fg)]">
          Mentor · sharing screen
        </span>
      </div>
      <div className="mt-2 grid grid-cols-6 gap-1.5">
        {tiles.map((tile) => (
          <span
            key={tile}
            className="flex aspect-square items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.04] font-code text-[8px] text-[var(--surface-muted)]"
          >
            {tile}
          </span>
        ))}
      </div>
    </div>
  )
}

export function SupportChat({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cn('space-y-2 text-[12px] leading-snug', className)}>
      <p className="w-fit max-w-[220px] rounded-xl rounded-tl-sm bg-white/[0.07] px-3 py-2 text-[var(--surface-fg)]">
        My API returns 401 after login 😩
      </p>
      <p className="ml-auto w-fit max-w-[230px] rounded-xl rounded-tr-sm bg-[#25D366]/15 px-3 py-2 text-green-100">
        Send the request headers. The token is probably missing &ldquo;Bearer&rdquo;.
      </p>
      <p className="w-fit max-w-[220px] rounded-xl rounded-tl-sm bg-white/[0.07] px-3 py-2 text-[var(--surface-fg)]">
        That was it. Working now ✓
      </p>
    </div>
  )
}

export function ProjectStack({ className }: { className?: string }) {
  const projects = [
    { title: 'Study planner app', tag: 'React Native · Expo', dot: 'bg-[var(--marker)]' },
    { title: 'SaaS dashboard', tag: 'Next.js · MongoDB', dot: 'bg-[var(--color-brand-accent)]' },
    { title: 'WhatsApp order agent', tag: 'n8n · OpenAI', dot: 'bg-[#25D366]' },
  ]
  const placement = [
    'z-0 -translate-x-1/2 translate-y-0 scale-[0.86] opacity-70 group-hover:-translate-x-[86%] group-hover:translate-y-3 group-hover:-rotate-6 group-hover:opacity-100',
    'z-10 -translate-x-1/2 translate-y-5 scale-[0.93] opacity-85 group-hover:translate-y-1 group-hover:opacity-100',
    'z-20 -translate-x-1/2 translate-y-11 group-hover:-translate-x-[14%] group-hover:translate-y-12 group-hover:rotate-6',
  ]
  return (
    <div aria-hidden="true" className={cn('relative h-36', className)}>
      {projects.map((project, index) => (
        <div
          key={project.title}
          className={cn(
            'absolute left-1/2 top-0 w-[76%] rounded-xl border border-white/10 bg-[#121519] p-3 shadow-[0_18px_40px_-16px_rgba(0,0,0,0.95)] transition-[transform,opacity] duration-500 ease-[var(--ease-out-expo)]',
            placement[index],
          )}
        >
          <p className="flex items-center gap-2 text-[12px] font-semibold text-[var(--text-strong)]">
            <span className={cn('size-1.5 rounded-full', project.dot)} />
            {project.title}
          </p>
          <p className="mt-0.5 font-code text-[10px] text-[var(--surface-muted)]">{project.tag}</p>
          <div className="mt-2.5 flex gap-1">
            <span className="h-1 w-8 rounded bg-white/20" />
            <span className="h-1 w-5 rounded bg-white/10" />
            <span className="h-1 w-10 rounded bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function InstallmentBar({
  first,
  second,
  className,
}: {
  first: string
  second: string
  className?: string
}) {
  return (
    <div aria-hidden="true" className={cn('space-y-2.5', className)}>
      <div className="flex h-3 overflow-hidden rounded-full bg-white/[0.06]">
        <span className="h-full w-1/2 bg-[linear-gradient(90deg,#0b9fbd,#00e5ff)] shadow-[0_0_14px_rgba(0,229,255,0.6)]" />
        <span className="ml-0.5 h-full w-1/2 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.14)_0_6px,rgba(255,255,255,0.05)_6px_12px)]" />
      </div>
      <div className="flex justify-between gap-3 font-code text-[10px] uppercase tracking-wider text-[var(--surface-muted)]">
        <span className="inline-flex items-center gap-1.5">
          <IconCheck size={11} className="text-[var(--color-brand-accent)]" /> {first}
        </span>
        <span>{second}</span>
      </div>
    </div>
  )
}

const PORTAL_STATES = ['done', 'done', 'done', 'current', 'locked', 'locked'] as const

export function PortalMock({
  courseTitle,
  modules,
  className,
}: {
  courseTitle: string
  modules: string[]
  className?: string
}) {
  const rows = modules.slice(0, PORTAL_STATES.length).map((title, index) => ({
    title: title.replace(/^Week \d+:\s*/, ''),
    week: index + 1,
    state: PORTAL_STATES[index],
  }))
  const current = rows.find((row) => row.state === 'current')

  return (
    <div
      aria-hidden="true"
      className={cn(
        'overflow-hidden rounded-[22px] border border-white/10 bg-[#0a0c0f] shadow-[0_50px_120px_-50px_rgba(0,0,0,0.95)]',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 border-b border-white/[0.06] bg-white/[0.02] px-4 py-3">
        <span className="size-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="size-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="size-2.5 rounded-full bg-[#28c840]/80" />
        <span className="ml-4 truncate rounded-full bg-white/[0.05] px-3 py-1 font-code text-[10px] text-[var(--surface-muted)]">
          skillo.minhajsadik.com/portal
        </span>
      </div>
      <div className="grid gap-4 p-4 sm:grid-cols-[1.25fr_1fr] sm:p-5">
        <div>
          <p className="font-code text-[10px] uppercase tracking-[0.18em] text-[var(--surface-muted)]">
            Your batch
          </p>
          <p className="mt-1.5 text-[14px] font-semibold leading-snug text-[var(--text-strong)]">
            {courseTitle}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.07]">
              <span className="block h-full w-[31%] rounded-full bg-[linear-gradient(90deg,#0b9fbd,#00e5ff)]" />
            </div>
            <span className="font-code text-[10px] text-[var(--color-brand-accent-readable)]">
              31%
            </span>
          </div>
          <ol className="mt-4 grid gap-1.5">
            {rows.map((row) => (
              <li
                key={row.week}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg border px-2.5 py-2 text-[11.5px] leading-tight',
                  row.state === 'current'
                    ? 'border-[var(--color-brand-accent)]/35 bg-[var(--color-brand-accent)]/[0.07] text-[var(--text-strong)]'
                    : row.state === 'locked'
                      ? 'border-dashed border-white/[0.08] bg-transparent text-[var(--surface-muted)]'
                      : 'border-white/[0.05] bg-white/[0.02] text-[var(--surface-fg)]',
                )}
              >
                <span
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full font-code text-[9px]',
                    row.state === 'done' && 'bg-green-400/15 text-green-300',
                    row.state === 'current' &&
                      'bg-[var(--color-brand-accent)]/20 text-[var(--color-brand-accent-readable)]',
                    row.state === 'locked' && 'bg-white/[0.06] text-[var(--surface-muted)]',
                  )}
                >
                  {row.state === 'done' ? <IconCheck size={10} /> : row.week}
                </span>
                <span className="min-w-0 flex-1 truncate">{row.title}</span>
                {row.state === 'locked' && (
                  <IconLock size={11} className="shrink-0 text-[var(--surface-muted)]" />
                )}
              </li>
            ))}
          </ol>
        </div>
        <div className="grid content-start gap-3">
          <div className="rounded-xl border border-green-400/20 bg-green-400/[0.05] p-3.5">
            <p className="font-code text-[9.5px] uppercase tracking-[0.16em] text-green-300">
              Module 3 test
            </p>
            <p className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-[26px] font-semibold leading-none text-[var(--text-strong)]">
                83%
              </span>
              <span className="text-[11px] font-semibold text-green-300">Passed</span>
            </p>
            <p className="mt-2 text-[10.5px] leading-snug text-[var(--surface-muted)]">
              10 of 12 marked right away. Review the two you missed.
            </p>
          </div>
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3.5">
            <p className="font-code text-[9.5px] uppercase tracking-[0.16em] text-[var(--surface-muted)]">
              Mentor feedback
            </p>
            <p className="mt-2 text-[11px] leading-snug text-[var(--surface-fg)]">
              &ldquo;Clean layout. Add a focus style to the buttons, then resubmit.&rdquo;
            </p>
          </div>
          {current && (
            <div className="rounded-xl border border-[var(--color-brand-accent)]/30 bg-[linear-gradient(135deg,rgba(0,229,255,0.12),rgba(0,229,255,0.03))] p-3.5">
              <p className="font-code text-[9.5px] uppercase tracking-[0.16em] text-[var(--color-brand-accent-readable)]">
                Next step
              </p>
              <p className="mt-2 text-[11.5px] font-semibold leading-snug text-[var(--text-strong)]">
                Week {current.week}: {current.title}
              </p>
              <span className="mt-3 inline-flex items-center rounded-full bg-[var(--color-brand-accent)] px-3 py-1 text-[10.5px] font-semibold text-[var(--accent-ink)]">
                Continue
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
